import { useEffect, useMemo, useRef, useState } from "react";

import { lerPreferencia } from "@/features/preferences/api";

/** Gravada pelo servidor a cada "Enviar meu painel": `{ em, de }`. */
export const CHAVE_PAINEL_RECEBIDO = "painel-recebido";

const CICLO_MS = 5 * 60_000;
/** Volta a aba confere na hora, mas nao mais que isto: trocar de aba nao vira rajada. */
const VOLTA_MINIMA_MS = 30_000;
/** Tela sem toque nem tecla por este tempo carrega sozinha (a TV da fabrica). */
const OCIOSO_MS = 60_000;
const PASSO_OCIOSO_MS = 10_000;
const USO = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

type Marca = { em: string; de?: string };
type Escopo = { userId: string | null };
type Aviso = { escopo: Escopo; tipo: "recebido" | "incerto"; de?: string };

function marcaDe(valor: unknown): Marca | null {
  if (!valor || typeof valor !== "object") return null;
  const { em, de } = valor as { em?: unknown; de?: unknown };
  return typeof em === "string" ? { em, de: typeof de === "string" ? de : undefined } : null;
}

/**
 * Painel enviado pelo admin chegando a uma tela JA ABERTA (U10).
 *
 * O painel inteiro (cards, nomes dos sinais, janelas, indicadores de turno) e
 * lido na carga da pagina, por varios hooks e com cache local. Em vez de
 * ensinar cada um a revalidar, a tela confere UMA marca por pessoa e, quando
 * ela muda, oferece carregar de novo — tudo chega junto e coerente.
 * Se a primeira consulta falha, uma marca lida depois nao prova quando o
 * painel mudou. Oferece conferir manualmente, sem afirmar um envio novo nem
 * comparar o relogio do navegador com o do servidor.
 *
 * Nao recarrega no meio do uso: uma recarga sem aviso perderia rascunho (regra
 * do produto, ver `RootErrorBoundary`). So recarrega sozinha com a tela parada
 * por 1 minuto, que e o caso da TV de chao de fabrica que ninguem toca.
 */
export function AvisoPainelRecebido({
  userId,
  recarregar = () => window.location.reload(),
}: {
  userId: string | null;
  recarregar?: () => void;
}) {
  // A identidade do escopo impede que aviso/timer voltem no percurso A -> B -> A.
  const escopo = useMemo(() => ({ userId }), [userId]);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const novo = aviso?.escopo === escopo ? aviso : null;
  const recarregarRef = useRef(recarregar);
  recarregarRef.current = recarregar;

  useEffect(() => {
    if (!escopo.userId) return;
    let vivo = true, encerrado = false, ultima = 0;
    // `undefined` = ainda sem leitura de referencia; `null` = nunca recebeu.
    let referencia: string | null | undefined;
    let referenciaFalhou = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | null = null;

    const agendar = () => {
      clearTimeout(timer);
      if (vivo && !encerrado && document.visibilityState !== "hidden") {
        timer = setTimeout(() => void conferir(), Math.max(0, ultima + CICLO_MS - Date.now()));
      }
    };
    async function conferir() {
      // A REFERENCIA e lida na carga mesmo em aba oculta: ela precisa
      // corresponder ao painel que esta pagina carregou. Lida so ao olhar a
      // aba, um envio feito antes disso viraria a referencia e nunca avisaria.
      const oculta = document.visibilityState === "hidden";
      if (!vivo || encerrado || controller || (oculta && referencia !== undefined)) return;
      clearTimeout(timer);
      ultima = Date.now();
      const tentativa = new AbortController();
      controller = tentativa;
      try {
        const marca = marcaDe(await lerPreferencia<unknown>(CHAVE_PAINEL_RECEBIDO, tentativa.signal));
        if (!vivo || controller !== tentativa) return;
        const em = marca?.em ?? null;
        // Primeira leitura normal e silenciosa. Depois de uma falha inicial,
        // a marca pode ser anterior OU posterior ao painel desta pagina.
        if (referencia === undefined) {
          referencia = em;
          if (referenciaFalhou && em !== null) setAviso({ escopo, tipo: "incerto" });
        }
        else if (em !== null && em !== referencia) {
          encerrado = true;
          setAviso({ escopo, tipo: "recebido", de: marca?.de });
        }
      } catch {
        // Silencioso: o aviso e conveniencia; a proxima volta do ciclo tenta de novo.
        if (vivo && controller === tentativa && referencia === undefined) referenciaFalhou = true;
      } finally {
        if (controller === tentativa) controller = null;
      }
      agendar();
    }
    const visibilidade = () => {
      if (document.visibilityState === "hidden") {
        clearTimeout(timer);
        // A leitura de referencia termina mesmo oculta; o ciclo, nao.
        if (referencia === undefined) return;
        const anterior = controller;
        controller = null;
        anterior?.abort();
      } else if (Date.now() - ultima >= VOLTA_MINIMA_MS) void conferir();
      else agendar();
    };
    document.addEventListener("visibilitychange", visibilidade);
    void conferir();
    return () => {
      vivo = false;
      clearTimeout(timer);
      controller?.abort();
      document.removeEventListener("visibilitychange", visibilidade);
    };
  }, [escopo]);

  useEffect(() => {
    // Incerteza sobre a primeira carga nunca autoriza descartar a tela atual.
    if (!novo || novo.tipo !== "recebido") return;
    let ultimoUso = Date.now();
    const uso = () => { ultimoUso = Date.now(); };
    for (const evento of USO) window.addEventListener(evento, uso, { passive: true });
    const passo = setInterval(() => {
      if (document.visibilityState !== "hidden" && Date.now() - ultimoUso >= OCIOSO_MS) {
        clearInterval(passo);
        recarregarRef.current();
      }
    }, PASSO_OCIOSO_MS);
    return () => {
      clearInterval(passo);
      for (const evento of USO) window.removeEventListener(evento, uso);
    };
  }, [novo]);

  if (!novo) return null;
  return (
    <div className="painel-recebido" role="status">
      <p>
        {novo.tipo === "incerto" ? "Não foi possível confirmar se este painel está atualizado." : <>
          <b>{novo.de ?? "A administração"}</b> enviou um painel novo.{" "}
          <span className="muted">Se a tela ficar parada por 1 minuto, ele carrega sozinho.</span>
        </>}
      </p>
      <button type="button" className="btn primary sm" onClick={() => recarregarRef.current()}>
        Carregar agora
      </button>
    </div>
  );
}
